import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const BRAND = '#4A7EB5';
const BG = '#0F1923';
const SURFACE = '#1A2B3C';
const BORDER = '#2D4259';
const MUTED = '#8FA8C0';

export default function SalesManagerDashboard() {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [training, setTraining] = useState<any[]>([]);
  const [pipeline, setPipeline] = useState<any[]>([]);
  const [invites, setInvites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/leaderboard'),
      api.get('/onboarding/training-tracker'),
      api.get('/jobs'),
      api.get('/onboarding/invites'),
    ]).then(([lb, tr, pl, inv]) => {
      setLeaderboard(lb.data.slice(0, 5));
      setTraining(tr.data);
      // Filter to the signed-in specialist’s jobs and active roofing stages
      setPipeline((pl.data || []).filter((j: any) =>
        j.assigned_specialist_id === user?.id || ['Full Approval', 'EagleView Ordered', 'EagleView Received', 'Materials Ordered', 'Job Scheduled'].includes(j.pipeline_stage_name)
      ).slice(0, 8));
      setInvites((inv.data || []).filter((i: any) => i.status !== 'activated').slice(0, 5));
    }).catch(console.error).finally(() => setLoading(false));
  }, [user]);

  const inTraining = training.filter(s => s.percentComplete < 100);
  const totalDoors = leaderboard.reduce((sum, s) => sum + (s.doorsKnocked || 0), 0);
  const totalSold  = leaderboard.reduce((sum, s) => sum + (s.sold || 0), 0);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: BG }}>
      <div style={{ color: MUTED, fontSize: 16 }}>Loading...</div>
    </div>
  );

  return (
    <div style={{ background: BG, minHeight: '100%', padding: 32 }}>

      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ color: '#fff', fontSize: 28, fontWeight: 800 }}>Good {greeting()}, {user?.name?.split(' ')[0] || 'team'} 👋</div>
        <div style={{ color: MUTED, fontSize: 14, marginTop: 4 }}>Here's where everything stands today.</div>
      </div>

      {/* Top Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
        <StatCard icon="👥" label="Active Salesmen"    value={leaderboard.length} color={BRAND} />
        <StatCard icon="📅" label="In Training"        value={inTraining.length}  color="#F59E0B" link="/training-tracker" />
        <StatCard icon="🚪" label="Doors Knocked"      value={totalDoors.toLocaleString()} color="#10B981" />
        <StatCard icon="💰" label="Jobs Sold (Team)"   value={totalSold} color="#10B981" />
        <StatCard icon="📊" label="Active Jobs"        value={pipeline.length} color={BRAND} link="/pipeline" />
        <StatCard icon="📋" label="Pending Onboarding" value={invites.length} color="#F59E0B" link="/employees" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

        {/* Training This Week */}
        <Section title="🗓️ First-Week Training" action={{ label: 'Full Tracker →', to: '/training-tracker' }}>
          {inTraining.length === 0 ? (
            <Empty text="No salesmen currently in training." />
          ) : inTraining.map(s => (
            <div key={s.id} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{s.name}</div>
                  <div style={{ color: MUTED, fontSize: 12 }}>Day {s.currentDay} of 5 · {s.completedModules}/{s.totalModules} modules</div>
                </div>
                <div style={{ color: BRAND, fontSize: 13, fontWeight: 700 }}>{s.percentComplete}%</div>
              </div>
              {/* Day pills */}
              <div style={{ display: 'flex', gap: 6 }}>
                {s.days.map((d: any) => (
                  <div key={d.day} style={{
                    flex: 1, textAlign: 'center', padding: '4px 0', borderRadius: 6, fontSize: 11, fontWeight: 700,
                    background: d.complete ? '#10B98122' : d.day === s.currentDay ? BRAND + '33' : '#0F1923',
                    color: d.complete ? '#10B981' : d.day === s.currentDay ? BRAND : MUTED,
                    border: `1px solid ${d.complete ? '#10B98144' : d.day === s.currentDay ? BRAND + '66' : BORDER}`,
                  }}>
                    {d.complete ? '✓' : `D${d.day}`}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Section>

        {/* Leaderboard Snapshot */}
        <Section title="🏆 Team Leaderboard" action={{ label: 'Full Board →', to: '/leaderboard' }}>
          {leaderboard.length === 0 ? <Empty text="No team data yet." /> : leaderboard.map((s, i) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < leaderboard.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
              <div style={{ fontSize: 18, width: 28, textAlign: 'center' }}>
                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{s.name}</div>
                <div style={{ color: MUTED, fontSize: 12 }}>🚪 {s.doorsKnocked} · 💰 {s.sold} sold · {s.conversionRate}%</div>
              </div>
              <div style={{ color: BRAND, fontWeight: 800, fontSize: 16 }}>{s.score.toLocaleString()} <span style={{ color: MUTED, fontSize: 10, fontWeight: 400 }}>pts</span></div>
            </div>
          ))}
        </Section>

        {/* Active Pipeline Jobs */}
        <Section title="📊 Active Pipeline Jobs" action={{ label: 'Full Pipeline →', to: '/pipeline' }}>
          {pipeline.length === 0 ? <Empty text="No active jobs." /> : pipeline.map((j: any) => (
            <Link key={j.id} to={`/pipeline/${j.id}`} style={{ textDecoration: 'none' }}>
              <div style={{ padding: '10px 12px', borderRadius: 10, background: '#0F1923', border: `1px solid ${BORDER}`, marginBottom: 8, cursor: 'pointer' }}>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{j.homeowner_name || j.address}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ color: MUTED, fontSize: 12 }}>{j.address}</div>
                  <div style={{ background: BRAND + '22', color: BRAND, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6 }}>{j.pipeline_stage_name}</div>
                </div>
              </div>
            </Link>
          ))}
        </Section>

        {/* Pending Onboarding */}
        <Section title="📋 Pending Onboarding" action={{ label: 'All Employees →', to: '/employees' }}>
          {invites.length === 0 ? <Empty text="No pending onboarding." /> : invites.map((inv: any) => (
            <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${BORDER}` }}>
              <div style={{ width: 36, height: 36, borderRadius: 18, background: BRAND + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', color: BRAND, fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
                {inv.name?.charAt(0)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{inv.name}</div>
                <div style={{ color: MUTED, fontSize: 12 }}>{ROLE_LABELS[inv.role] || inv.role}</div>
              </div>
              <StatusPill status={inv.status} />
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  salesman: 'Field Sales Rep', estimator: 'Estimator',
  foreman: 'Field Foreman', admin: 'Administrator',
};

const STATUS_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  invited:     { bg: '#F59E0B22', color: '#F59E0B', label: 'Invited' },
  in_progress: { bg: '#4A7EB522', color: '#4A7EB5', label: 'In Progress' },
  completed:   { bg: '#10B98122', color: '#10B981', label: 'Completed' },
  expired:     { bg: '#EF444422', color: '#EF4444', label: 'Expired' },
};

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
}

function StatCard({ icon, label, value, color, link }: any) {
  const inner = (
    <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: '18px 20px' }}>
      <div style={{ fontSize: 24, marginBottom: 8 }}>{icon}</div>
      <div style={{ color, fontSize: 28, fontWeight: 800 }}>{value}</div>
      <div style={{ color: MUTED, fontSize: 12, marginTop: 4 }}>{label}</div>
    </div>
  );
  return link ? <Link to={link} style={{ textDecoration: 'none' }}>{inner}</Link> : inner;
}

function Section({ title, children, action }: any) {
  return (
    <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 16, padding: '20px 22px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>{title}</div>
        {action && <Link to={action.to} style={{ color: BRAND, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>{action.label}</Link>}
      </div>
      {children}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const cfg = STATUS_COLORS[status] || { bg: '#33333322', color: '#888', label: status };
  return (
    <div style={{ background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 8 }}>{cfg.label}</div>
  );
}

function Empty({ text }: { text: string }) {
  return <div style={{ color: MUTED, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>{text}</div>;
}
